import { makeStyles } from "@material-ui/core";

/**
 * Shared MUI styles used by CheckoutFormPanel and FormFieldRenderer.
 * Kept in one place so both files reference identical class definitions
 * (same green brand, same padding, same error states).
 */

const BRAND = "#04cf66";
const BRAND_HOVER = "#03b85a";
const BORDER = "#E5E5E5";
// Near-black used by the secondary (Request Callback) CTA so it reads as
// subordinate to the green Pay button instead of competing with it.
const INK = "#111814";

export const COLORS = { BRAND, BRAND_HOVER, BORDER, INK };

export const useOfferCheckoutStyles = makeStyles((theme) => ({
  /* ---- page variant wrapper (full-page layout) ---- */
  pageRoot: {
    // 100vh on iOS Safari means the LARGE viewport (URL bar hidden), so the
    // page ends up taller than what's actually visible and leaves dead space
    // below the content. dvh tracks the real visible height; vh stays as the
    // fallback for browsers without dvh.
    minHeight: "100vh",
    "@supports (min-height: 100dvh)": { minHeight: "100dvh" },
    backgroundColor: "#ffffff",
    paddingBottom: theme.spacing(6),
    // On mobile the sticky bar's spacer already reserves the clearance, so
    // this padding would just stack more blank space under the form.
    [theme.breakpoints.down("sm")]: { paddingBottom: 0 },
    position: "relative",
    "&:before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "180px",
      background: `linear-gradient(180deg, rgba(4,207,102,0.10) 0%, rgba(4,207,102,0) 100%)`,
      zIndex: 0,
      pointerEvents: "none",
    },
  },
  pageHeader: {
    position: "relative",
    zIndex: 1,
    maxWidth: 520,
    margin: "0 auto",
    padding: theme.spacing(3, 2, 3),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  pageLogo: { height: 32 },
  pageContainer: {
    position: "relative",
    zIndex: 1,
    maxWidth: 520,
    margin: "0 auto",
    padding: theme.spacing(0, 2),
  },

  /* ---- plan card ---- */
  planCard: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
    border: `1px solid ${BORDER}`,
    position: "relative",
    overflow: "hidden",
  },
  planHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
  },
  productName: {
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
    fontSize: "18px",
    fontWeight: 700,
    color: "#000000",
    lineHeight: 1.3,
  },
  tagline: {
    fontSize: "14px",
    color: "#696969",
    marginBottom: theme.spacing(1.5),
  },
  discountBadge: {
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    backgroundColor: BRAND,
    color: "#ffffff",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  /**
   * Billing-cycle switch — a segmented control sitting between the product
   * name and the price, so the number visibly belongs to the selected tab.
   * Only rendered when a page ships more than one billing option.
   */
  // Duration selector rendered as radios — a flat white chip per cycle with a
  // radio dot, so the clickable intent is unmistakable (replaces the old
  // segmented toggle). Unselected chips carry no visible border (it fought the
  // borderless card); only the selected chip gets the green outline. The
  // transparent border reserves that 1.5px so selecting one never shifts the
  // row. Options grow equally to fill the card width.
  billingRadioGroup: {
    display: "flex",
    width: "100%",
    boxSizing: "border-box",
    gap: "8px",
    marginBottom: "16px",
  },
  billingRadioOption: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
    padding: "10px 12px",
    borderRadius: "10px",
    border: "1.5px solid transparent",
    backgroundColor: "#ffffff",
    fontFamily: "inherit",
    textAlign: "left",
    color: INK,
    transition: "border-color .18s ease, background-color .18s ease",
    WebkitTapHighlightColor: "transparent",
    "&:hover": { backgroundColor: "#F3F6F4" },
    "&:focus-visible": { outline: `2px solid ${BRAND}`, outlineOffset: "1px" },
  },
  billingRadioOptionActive: {
    borderColor: BRAND,
    backgroundColor: "rgba(4,207,102,0.06)",
    "&:hover": { backgroundColor: "rgba(4,207,102,0.06)" },
  },
  // The radio circle; fills with brand green when its option is selected.
  billingRadioDot: {
    flexShrink: 0,
    width: "18px",
    height: "18px",
    borderRadius: "50%",
    border: "2px solid #C4CCC8",
    boxSizing: "border-box",
    position: "relative",
    transition: "border-color .18s ease",
  },
  billingRadioDotActive: {
    borderColor: BRAND,
    "&::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      left: "50%",
      width: "9px",
      height: "9px",
      borderRadius: "50%",
      backgroundColor: BRAND,
      transform: "translate(-50%, -50%)",
    },
  },
  billingRadioText: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    minWidth: 0,
    lineHeight: 1.2,
  },
  billingSwitchLabel: {
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: 1.2,
  },
  // Effective per-month price under each cycle name. Muted so it stays quiet
  // next to the cycle label.
  billingSwitchPerMonth: {
    fontSize: "11px",
    fontWeight: 500,
    lineHeight: 1.2,
    color: "inherit",
    opacity: 0.72,
    fontVariantNumeric: "tabular-nums",
  },
  priceRow: {
    display: "flex",
    alignItems: "baseline",
    gap: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.5),
    borderBottom: `1px solid ${BORDER}`,
    marginBottom: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  price: {
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
    fontSize: "32px",
    fontWeight: 700,
    // Near-black headline: reads as the definitive amount rather than another
    // green accent competing with the CTA. Matches the product-name ink.
    color: "#000000",
    lineHeight: 1,
  },
  mrp: {
    fontSize: "15px",
    textDecoration: "line-through",
    color: "#9e9e9e",
  },
  gstNote: {
    fontSize: "12px",
    color: "#9e9e9e",
    fontWeight: 500,
    marginLeft: theme.spacing(-0.5),
  },
  cycle: {
    fontSize: "13px",
    color: "#696969",
    marginLeft: "auto",
  },
  kitOption: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    marginTop: 12,
    padding: "12px 14px",
    border: "1px solid #d7e3da",
    borderRadius: 10,
    background: "#f6fbf8",
    cursor: "pointer",
  },
  kitCheckbox: {
    marginTop: 2,
    width: 18,
    height: 18,
    accentColor: "#04cf66",
    cursor: "pointer",
    flexShrink: 0,
  },
  kitLabel: {
    fontSize: 13,
    lineHeight: 1.4,
    color: "#274233",
  },
  bonus: {
    backgroundColor: "#E3FFCC",
    color: "#236E34",
    padding: theme.spacing(1, 1.25),
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    border: `1px solid ${BRAND}55`,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
  },
  /* ---- value stack + benefits accordion ---- */
  /* "AED 444 Total Value" beside the charged amount. */
  totalValue: {
    fontSize: "14px",
    fontWeight: 600,
    color: BRAND,
    textDecoration: "line-through",
    textDecorationColor: "rgba(4,207,102,0.9)",
  },
  /**
   * Value stack as rounded pills — one per component of what the buyer gets.
   * Flows inline and wraps naturally; the card is 448px wide so three pills
   * land as 2 + 1 rather than one row.
   */
  pillRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 5,
    marginBottom: theme.spacing(1.5),
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 9px",
    border: `1px solid ${BORDER}`,
    borderRadius: 999,
    backgroundColor: "#ffffff",
    fontSize: "12px",
    fontWeight: 600,
    color: "#1a1a1a",
    lineHeight: 1.2,
    whiteSpace: "nowrap",
  },
  /* Opt-in `highlight` pill: calls out a bonus inclusion (BIL-409). */
  pillHighlight: {
    backgroundColor: "#E7FAEF",
    border: `1px solid ${BRAND}`,
    color: "#036B36",
  },
  /* Opt-in `originalAmount`: the list price, struck through before the
     discounted amount. */
  pillStrike: {
    textDecoration: "line-through",
    color: "#9a9a9a",
    fontWeight: 500,
  },
  /**
   * A thin glyph, NOT the filled CheckCircleIcon used in the list below —
   * inside a small capsule the filled disc reads as heavy and makes the pills
   * look like list items wearing a border.
   */
  pillCheck: {
    color: BRAND,
    fontSize: "12px",
    fontWeight: 700,
    lineHeight: 1,
    flexShrink: 0,
  },
  /* Toggle under the shortened benefits list. */
  benefitsToggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    marginTop: theme.spacing(1.25),
    padding: 0,
    background: "none",
    border: "none",
    color: BRAND,
    fontSize: "13px",
    fontWeight: 600,
    fontFamily: "inherit",
    cursor: "pointer",
    "&:hover": { textDecoration: "underline" },
  },
  includesList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "grid",
    gap: theme.spacing(1),
  },
  includeItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    fontSize: "14px",
    color: "#000000",
    lineHeight: 1.4,
  },
  checkIcon: {
    color: BRAND,
    fontSize: 18,
    flexShrink: 0,
    marginTop: 1,
  },

  /* ---- selectable product cards (multi-group combo pages ONLY) ---- */
  groupIntro: {
    fontSize: "13px",
    color: "#696969",
    textAlign: "center",
    margin: theme.spacing(0, 0, 2),
  },
  planCardSelectable: {
    cursor: "pointer",
    // Doubled selector so the 2px selectable border outranks cardFlat's
    // `border: none` (both are single classes on the same element, so
    // specificity — not source order — has to decide the winner).
    "&&": { border: "2px solid transparent" },
    overflow: "visible",
    transition:
      "border-color .15s ease, box-shadow .15s ease, opacity .15s ease",
    "&:focus-visible": { outline: "2px solid #04CF66", outlineOffset: "2px" },
  },
  planCardSelected: {
    // Green border marks the chosen card; no shadow (the flat design stays
    // shadowless — cardFlat's boxShadow:none would win anyway). Selection is
    // read from the border + filled radio + the dimming of the other card.
    "&&": { borderColor: BRAND },
  },
  planCardDimmed: { opacity: 0.72 },
  planCardRadio: {
    position: "absolute",
    top: 18,
    right: 16,
    width: 22,
    height: 22,
    borderRadius: "50%",
    border: "2px solid #cfd3d8",
    backgroundColor: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    flexShrink: 0,
  },
  planCardRadioSelected: {
    borderColor: BRAND,
    "&::after": {
      content: '""',
      width: 11,
      height: 11,
      borderRadius: "50%",
      backgroundColor: BRAND,
    },
  },
  planCardBadge: {
    position: "absolute",
    top: -10,
    left: 20,
    backgroundColor: BRAND,
    color: "#ffffff",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    padding: "4px 10px",
    borderRadius: 20,
    whiteSpace: "nowrap",
  },

  /* ---- form ---- */
  formCard: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    padding: theme.spacing(2.5),
    boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
    border: `1px solid ${BORDER}`,
  },
  formTitle: {
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
    fontSize: "16px",
    fontWeight: 700,
    color: "#000000",
    marginBottom: theme.spacing(2),
  },
  formFieldLabel: {
    marginBottom: "6px",
    fontWeight: 500,
    fontSize: "14px",
    color: "#000000",
    [theme.breakpoints.down("sm")]: { fontSize: "13px" },
  },
  /* Organisation-size radios — same design as the ind-plans-1 checkout:
     a visible radio in a bordered row, two per line, one per line on a
     phone. Values copied from RegionalPlansPage so the two pages match. */
  radioGroup: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.up("md")]: {
      marginBottom: theme.spacing(2),
    },
  },
  radioButton: {
    flex: "0 0 calc(50% - 5px)",
    margin: 0,
    [theme.breakpoints.down("xs")]: {
      flex: "0 0 100%",
    },
  },
  radioLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "92%",
    padding: "8px 12px",
    // Fainter hairline + softer 12px corners (founder direction).
    border: "0.5px solid rgba(224,224,224,0.23)",
    borderRadius: "12px",
    transition: "all 0.2s ease",
    cursor: "pointer",
    backgroundColor: "#FFFFFF",
    "&:hover": {
      backgroundColor: "#f9f9f9",
      borderColor: "#cccccc",
    },
  },
  radioLabelSelected: {
    backgroundColor: "#f0f9f1",
    borderColor: BRAND,
    "& .MuiFormControlLabel-label": {
      fontWeight: 500,
    },
  },
  radioRoot: {
    padding: "0",
    marginRight: "8px",
    color: BRAND,
  },
  radioErrorText: {
    color: "#f44336",
    fontSize: "12px",
    marginTop: "4px",
    marginLeft: "14px",
  },
  formField: {
    marginBottom: theme.spacing(1.5),
    "& .MuiOutlinedInput-root": {
      borderRadius: "0px",
      backgroundColor: "transparent",
      "& fieldset": {
        borderColor: "rgba(0, 0, 0, 0.23)",
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: "rgba(0, 0, 0, 0.87)",
        borderWidth: "1px",
      },
      "&.Mui-focused fieldset": {
        borderColor: BRAND,
        borderWidth: "1px",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "15px 14px",
      "&::placeholder": { color: "rgba(0, 0, 0, 0.6)", opacity: 1 },
      [theme.breakpoints.up("md")]: { padding: "12px 14px" },
    },
    "&.MuiTextField-root": {
      padding: "0px 4px",
      backgroundColor: "#FFFFFF",
      borderRadius: "4px",
      border: `0.5px solid ${BRAND}`,
    },
  },
  phonePrefix: {
    fontSize: "15px",
    color: "#000000",
    fontWeight: 500,
    paddingRight: theme.spacing(1),
    borderRight: `1px solid ${BORDER}`,
    marginRight: theme.spacing(1),
  },
  countrySearchRow: {
    backgroundColor: "#FFFFFF",
    padding: "4px 12px 8px",
    lineHeight: "normal",
    zIndex: 2,
  },

  /* Pay Now sits beside "Request a Callback" when the page enables it. */
  ctaRow: {
    display: "flex",
    gap: 10,
    alignItems: "stretch",
    marginTop: theme.spacing(1),
    // submitButton carries its own marginTop for the single-button case;
    // inside the row the parent owns the spacing. Selector is `> button`
    // rather than `> *` so it outranks .submitButton — equal specificity
    // would lose on source order and leave the pair misaligned by 8px.
    "& > button": { marginTop: 0 },
  },
  /**
   * Secondary action, equal width to Pay Now and in brand green so the pair
   * reads as one considered choice rather than a button plus an afterthought.
   */
  callbackButton: {
    flex: 1,
    minWidth: 0,
    padding: theme.spacing(1.5),
    // Deliberately NOT brand green: the secondary action sits beside the
    // green Pay button, and two green CTAs compete. Near-black outline keeps
    // it clearly secondary while staying high-contrast.
    backgroundColor: "transparent",
    color: INK,
    border: `1px solid ${INK}`,
    "&:hover": { backgroundColor: "rgba(17,24,20,0.06)" },
    "&:disabled": { opacity: 0.6 },
    borderRadius: "4px",
    fontWeight: 600,
    fontSize: "15px",
    textTransform: "none",
    height: "48px",
    whiteSpace: "nowrap",
  },
  ctaPrimary: {
    flex: 1,
    width: "auto",
    minWidth: 0,
  },
  submitButton: {
    padding: theme.spacing(1.5),
    backgroundColor: BRAND,
    color: "#ffffff",
    "&:hover": { backgroundColor: BRAND_HOVER },
    "&:disabled": {
      backgroundColor: BRAND,
      opacity: 0.7,
      color: "#ffffff",
    },
    borderRadius: "4px",
    fontWeight: 600,
    fontSize: "16px",
    textTransform: "none",
    marginTop: theme.spacing(1),
    height: "48px",
    width: "100%",
    boxShadow: "0 2px 8px rgba(4,207,102,0.25)",
  },

  /* ---- loading / error states ---- */
  loadingRoot: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),
    backgroundColor: "#ffffff",
  },
  loadingLogo: { height: 40, opacity: 0.9 },
  errorRoot: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
    backgroundColor: "#ffffff",
  },
  errorCard: {
    maxWidth: 380,
    width: "100%",
    textAlign: "center",
    padding: theme.spacing(3),
    borderRadius: 12,
    border: `1px solid ${BORDER}`,
    boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
  },
  errorHeading: {
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
    fontSize: 18,
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    color: "#000000",
  },
  errorMessage: {
    fontSize: 14,
    color: "#696969",
    marginBottom: theme.spacing(2),
  },
  retryButton: {
    backgroundColor: BRAND,
    color: "#ffffff",
    textTransform: "none",
    fontWeight: 600,
    "&:hover": { backgroundColor: BRAND_HOVER },
  },

  /* ---- mobile sticky pay bar ----
   * Mirrors the webinar checkout's sticky bar (webinarCheckout.styles.js) so
   * both single-page checkouts behave identically on phones. Desktop is
   * untouched: the bar is display:none and the in-form CTA stays visible.
   */
  // Hides the in-form CTA on mobile — the sticky bar replaces it, so the
  // buyer never sees two Pay buttons at once.
  hideOnMobile: {
    [theme.breakpoints.down("sm")]: { display: "none" },
  },
  // Reserves the bar's height so the fixed bar can't cover the last field
  // or the support block.
  stickySpacer: {
    display: "none",
    [theme.breakpoints.down("sm")]: { display: "block", height: "84px" },
  },
  stickyBar: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1.5),
      position: "fixed",
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 1200,
      backgroundColor: "#ffffff",
      // A tinted hairline instead of a grey one: the bar reads as a fixed
      // payment surface rather than the bottom edge of another card.
      borderTop: `1px solid rgba(4,207,102,0.25)`,
      boxShadow: "0 -6px 20px rgba(16,24,40,0.10)",
      padding: theme.spacing(1.5, 2),
      // iOS safe-area so the CTA isn't under the home indicator.
      paddingBottom: `calc(${theme.spacing(1.25)}px + env(safe-area-inset-bottom))`,
    },
  },
  stickyPriceWrap: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    lineHeight: 1.1,
    minWidth: 0,
  },
  stickyPriceRow: {
    display: "flex",
    alignItems: "baseline",
    gap: theme.spacing(0.75),
    flexWrap: "nowrap",
    minWidth: 0,
  },
  stickyPrice: {
    fontFamily: "'Inter', 'Helvetica', 'Arial', sans-serif",
    fontSize: "21px",
    fontWeight: 700,
    // Black to match the in-card headline price (same amount, same colour).
    color: "#000000",
    lineHeight: 1.15,
    // The amount must never wrap or re-flow: it changes when the QR kit is
    // toggled, and tabular figures keep the digits from jittering.
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "-0.01em",
  },
  stickyMrp: {
    fontSize: "13px",
    textDecoration: "line-through",
    color: "#9e9e9e",
  },
  stickyGstNote: {
    fontSize: "11px",
    color: "#9e9e9e",
    fontWeight: 500,
  },
  /* "(₹1,925 + GST)" under the sticky bar's headline amount — mirrors the
   * plan card's priceSubNote so both read the same. */
  stickySubNote: {
    fontSize: "11px",
    color: "#6B7280",
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  /* Sticky-bar variants: with the price gone the two CTAs split the strip
     evenly, and the callback picks up the bar's 8px radius so the pair
     matches. */
  stickyCallback: { borderRadius: "8px", flex: 1, minWidth: 0, fontSize: "14px" },
  stickyButtonWide: { flex: 1, minWidth: 0 },
  stickyButton: {
    backgroundColor: BRAND,
    color: "#ffffff",
    "&:hover": { backgroundColor: BRAND_HOVER },
    "&:disabled": { backgroundColor: BRAND, opacity: 0.7, color: "#ffffff" },
    fontWeight: 600,
    fontSize: "15px",
    textTransform: "none",
    borderRadius: "8px",
    padding: theme.spacing(1.5, 2.5),
    minHeight: 48,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxShadow: "0 2px 8px rgba(4,207,102,0.30)",
  },

  /* ---- flat page variant (opt-in via config `flatPageStyle`) ----
   * Plain white page — no green hero gradient — and 18px card corners.
   * Applied as extra classes so the pages that don't opt in keep the
   * gradient and 12px corners they ship with today.
   */
  pageRootFlat: {
    "&:before": { display: "none" },
  },
  // Founder's flat redesign, scoped to flatPageStyle pages so the legacy
  // automation-lite / showroom pages keep their bordered, shadowed cards and
  // their green-bordered fields / price divider untouched. Borderless +
  // shadowless, softer 18px corners, a faint grey fill so the card still
  // reads on white. The nested rules gate the field + divider changes to the
  // flat pages too: both cards carry this class, so the extra descendant
  // class raises specificity just enough to win over the base rules.
  cardFlat: {
    borderRadius: "18px",
    border: "none",
    boxShadow: "none",
    // Founder's grey at ~87% alpha (#f9f9f9de) — the original 58% washed out
    // to near-white and the cards vanished.
    background: "#f9f9f9de",
    "& $priceRow": { borderBottom: "none" },
    "& $formField": {
      "&.MuiTextField-root": { border: "none", borderRadius: "12px" },
    },
  },

  /* Small "(₹1,925 + GST)" line under a GST-inclusive headline price.
   * Lives inside the flex priceRow with a full-width basis so it wraps onto
   * its own line directly under the amount — and stays above priceRow's
   * bottom divider rather than drifting into the feature list. */
  priceSubNote: {
    flexBasis: "100%",
    fontSize: "13px",
    color: "#696969",
    fontWeight: 500,
    marginTop: 2,
  },
}));
